// src/pages/providers/5_patientOverview/PatientOverview.js
import React, { useEffect, useContext, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import Layout from '../../../components/Layout';
import { InformationCircleIcon } from '@heroicons/react/solid';
import { PatientDataContext } from '../../../context/PatientDataContext';
import PatientInfoPanel from './components/PatientInfoPanel';
import Breadcrumb from '../../../components/Breadcrumb';
import StatusMessage from '../../../components/StatusMessage';
import AssessmentModuleCard from './components/AssessmentModuleCard';
import PreAppointmentDashboardWithData from './components/PreAppointmentDashboardWithData';
import { isADHDProvisionalPrimaryDiagnosis, getPadStaleness } from '../../../utils/conditionChecks';
import PadRedoDialog from '../../../components/PadRedoDialog';
import useAnalytics from '../../../hooks/useAnalytics';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { derivePatientOverviewConfig } from './config/patientOverviewConfig';
import { getAssessmentModuleCardWidthClass } from './utils/getAssessmentModuleCardWidthClass';
import { buildCIExclusionsContext, computeCIProgress } from '../../../utils/clinicalInterviewWorkflow';
import { getDiagnosticDecisionStartRoute, computeDDModuleProgress } from '../../../utils/diagnosticDecisionRouting';
import { isSlamOrg } from '../2_dashboard/config/patientSetupConfig';
import AssignCliniciansDialog from './components/AssignCliniciansDialog';
import { toast } from 'react-toastify';
import SSIOverrideDialog from '../../../components/SSIOverrideDialog';
import { TriggerOverrideSSI } from '../../../utils/TriggerOverrideSSI';
import { TriggerReverseSSIOverride } from '../../../utils/TriggerReverseSSIOverride';
import { isNonProdEnvironment } from '../../../utils/environmentTheme';
import {
  isLeadClinician as checkIsLeadClinician,
  isSSILeadOverrideActive,
  getEffectiveSSIClinician,
} from '../../../utils/clinicianPermissions';
import {
  shouldLockAdhdDiagnosticDecision,
  shouldLockAdhdPostAssessmentDiscussion,
  shouldLockAdhdSemiStructuredInterview,
  getAdhdDiagnosticDecisionLockedTooltip,
  getAdhdSemiStructuredInterviewLockedTooltip,
  getAdhdPostAssessmentDiscussionLockedTooltip,
} from '../../../utils/moduleUnlockUtils';
import AssessmentEndedBanner from '../../../components/AssessmentEndedBanner';
import AppointmentDateDialog from './components/AppointmentDateDialog';
import {
  resolveDisplayAppointmentDate,
  // Module config key → appointmentDates short key, shared with the dialog and
  // the other Patient Overviews (consistent with STAGE_SHORT_NAMES in PatientTable.js).
  MODULE_KEY_TO_SHORT_KEY,
} from '../../../utils/appointmentDateHelpers';

// Module config key → dataModel stage key (for firstCompletedAt inference).
const MODULE_KEY_TO_STAGE_KEY = {
  clinicalInterview: '2_clinicalInterview',
  semiStructuredInterview: '3_semiStructuredInterview',
  diagnosticDecision: '4_diagnosticDecision',
  postAssessmentDiscussion: '5_postAssessmentDiscussion',
};

function PatientOverview() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, flushPendingUpdates } = useContext(PatientDataContext);
  const [PAProgress, setPAProgress] = useState(0);
  const [SSIProgress, setSSIProgress] = useState(0);
  const [PADProgress, setPADProgress] = useState(0);
  const [DDProgress, setDDProgress] = useState(0);
  const [informantData, setInformantData] = useState({});
  const { trackEvent } = useAnalytics();
  const { claims, user, isOrgAdmin, isSupportUser } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

 // Add state for post-CI ADHD-specific sections visibility  
 const [isAdhdAfterCI, setIsAdhdAfterCI] = useState(true); // Default to true initially

 // When set, tells PatientInfoPanel to open ReassignCliniciansDialog at this step.
 // Used by the "+" button on the DD module card to jump straight to 'addDDSupporting'.
 const [requestReassignStep, setRequestReassignStep] = useState(null);

 // Controls whether the AssignCliniciansDialog is open (SLaM Lead assignment from module cards).
 const [showAssignLeadDialog, setShowAssignLeadDialog] = useState(false);

 // Lead SSI override dialog (SLaM / 3 Borough) — opened from the SSI module card.
 const [showSSIOverrideDialog, setShowSSIOverrideDialog] = useState(false);

 // Appointment date dialog open state (SLaM / 3 Borough via org workflow config):
 //   null  = closed
 //   'all' = open with no module auto-focused (Actions → Manage Appointment Dates)
 //   short key ('ci'|'ssi'|'dd'|'pad') = open and focus that module's date input
	const [appointmentDateDialogModule, setAppointmentDateDialogModule] = useState(null);
	const [isOverridingSSI, setIsOverridingSSI] = useState(false);
	const [isReversingSSI, setIsReversingSSI] = useState(false);

	// SLaM PAD redo (v2) — opened from the Post-Assessment Discussion card when
	// the completed discussion no longer matches the finalised Diagnostic Decision
	// and the clinician chooses to redo it (straight to the redo details step).
	const [showPadRedoDialog, setShowPadRedoDialog] = useState(false);


 // Derive patient overview config from the centralised workflow config.
 // Once a Path-1 PAD has been redone for a changed Diagnostic Decision, the
 // reopened discussion follows the formal DD, so the overview reverts to the
 // canonical DD-third / PAD-fourth order (see derivePatientOverviewConfig).
 const organisationName = claims?.organisation || 'default';
 const padRedone = Boolean(
   patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.redo?.lastRedoAt
 );
	const config = derivePatientOverviewConfig(wf, { padRedone });

	// SLaM PAD staleness cue. When a completed Post-Assessment Discussion no longer
	// matches the finalised Diagnostic Decision, we surface a two-state advisory on
	// the PAD card: an amber "alert" until the clinician decides, then a quieter
	// "acknowledged" note once they have knowingly chosen to leave it as is. Gated
	// to SLaM (the only Path-1-capable workflow in v1).
	const padStaleness = getPadStaleness(patientDataModel);
	const showPadStalenessCue = wf.isSLaMWorkflow && padStaleness.isStale;
	const padMismatchAcknowledged = Boolean(
		patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.padMismatchAcknowledgedAt
	);
	// Once a redo has archived the original discussion, keep a persistent
	// read-only link to it (resolved by the PAD snapshot viewer route). This must
	// NOT be gated on the live staleness cue — after redo, isPadStaleVsDd is
	// false, so clinicians would otherwise lose both the note and the archive link.
	const padRedoRecord =
		patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.redo || null;
	const padRedoArchiveId = padRedoRecord?.lastRedoArchiveId || null;
	const showPadRedoneNote =
		wf.isSLaMWorkflow && Boolean(padRedoArchiveId) && !showPadStalenessCue;
	
 // Check if this is a subclinical service organization
 const isSubclinicalService = organisationName === 'ThinkDivergent';

 // Track page view when component mounts
 useEffect(() => {
   if (patientId) {
     trackEvent('patient_overview_viewed', {
       patientId,
       timestamp: new Date().toISOString()
     });
   }
 }, [patientId, trackEvent]);

// Fetch informant data when patientDataModel loads
// Fetch informant documents for the CURRENT patient only.
// Design notes:
// - We immediately reset informantData on patient switch to avoid stale cross-patient bleed.
// - We guard with isCurrent to prevent out-of-order async responses from previous requests
//   overwriting state for the new patient (classic race condition when navigating fast).
// - The effect depends on patientId and the informant mapping; any change re-fetches.
useEffect(() => {
  let isCurrent = true;

  // Always clear previous informant data when switching patients or informant mapping changes
  setInformantData({});

  const fetchInformantData = async () => {
    // If no informants for this patient, keep empty state to avoid stale display
    if (!patientDataModel?.informants || Object.keys(patientDataModel.informants).length === 0) {
      return;
    }

    const nextInformantsData = {};

    // Get informant document IDs from patient data
    const informantIds = Object.keys(patientDataModel.informants);
    for (const informantId of informantIds) {
      try {
        const informantRef = doc(db, 'patients', informantId);
        const informantDoc = await getDoc(informantRef);

        if (!isCurrent) return; // If patient changed mid-flight, discard this response

        if (informantDoc.exists()) {
          nextInformantsData[informantId] = informantDoc.data();
        }
      } catch (error) {
        console.error(`Error fetching informant data for ${informantId}:`, error);
      }
    }

    if (isCurrent) {
      setInformantData(nextInformantsData);
    }
  };

  fetchInformantData();

  return () => {
    isCurrent = false;
  };
}, [patientId, patientDataModel?.informants]);

 // Check for ADHD diagnosis
useEffect(() => {
  const useAdhdFlow = isADHDProvisionalPrimaryDiagnosis(patientDataModel);
  console.log('Using ADHD Flow:', useAdhdFlow)
  setIsAdhdAfterCI(useAdhdFlow);
  
  // COMMENTED OUT: Track the diagnosis flow determination
  /* 
  if (patientDataModel) {
    trackEvent('diagnosis_flow_determined', {
      patientId,
      isAdhdFlow: useAdhdFlow,
      primaryDiagnosis: patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.['6']?.data?.provisionalPrimaryDiagnosis
    });
  }
  */
}, [patientDataModel, patientId, trackEvent]);
  
  // Helper function to determine if module should be disabled
  const shouldDisableModule = (moduleKey) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig) return false;
    
    // If module is always visible, it's never disabled due to ADHD
    if (moduleConfig.alwaysVisible) return false;
    
    // Check if should disable when not ADHD
    if (moduleConfig.disableWhenNotAdhd && !isAdhdAfterCI) return true;
    
    return false;
  };

  // Helper function to determine if module should be locked.
  // SLaM / 3 Borough: after CI is first completed, DD and PAD are accessible
  // even when CI/SSI AI outputs are still awaiting Documents approval
  // (see moduleUnlockUtils — keeps overview aligned with Path 1/2 finish writes).
  const shouldLockModule = (moduleKey) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig) return false;
    
    switch (moduleKey) {
      case 'semiStructuredInterview':
        // One-clinician SLaM: SSI waits for the Lead's "Proceed" decision at
        // the end of the CI and is locked while the assessment is ended.
        return (
          shouldDisableModule(moduleKey) ||
          shouldLockAdhdSemiStructuredInterview({ patientDataModel, wf })
        );
      
      case 'diagnosticDecision':
        return shouldLockAdhdDiagnosticDecision({
          patientDataModel,
          wf,
          isSsiDisabled: shouldDisableModule('semiStructuredInterview'),
        });
      
      case 'postAssessmentDiscussion':
        return shouldLockAdhdPostAssessmentDiscussion({
          patientDataModel,
          wf,
          isPadDisabled: shouldDisableModule(moduleKey),
        });
      
      default:
        return false;
    }
  };

  // Helper function to get disabled reason
  const getDisabledReason = (moduleKey) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig || !shouldDisableModule(moduleKey)) return undefined;
    
    return "Not applicable as provisional primary diagnosis is not ADHD";
  };

  /**
   * Builds a dynamic tooltip string for locked modules explaining what has to
   * happen before the module can be unlocked. The Diagnostic Decision card is
   * gated behind CI and (optionally) SSI approval for non-SLaM orgs (SLaM
   * unlocks DD after CI complete). The one-clinician SLaM workflow also
   * explains SSI and PAD locks (awaiting the end-of-CI decision, awaiting SSI
   * completion, or assessment ended).
   */
  const getLockedTooltip = (moduleKey) => {
    switch (moduleKey) {
      case 'semiStructuredInterview':
        return getAdhdSemiStructuredInterviewLockedTooltip({ patientDataModel, wf });
      case 'diagnosticDecision':
        return getAdhdDiagnosticDecisionLockedTooltip({
          patientDataModel,
          wf,
          isSsiDisabled: shouldDisableModule('semiStructuredInterview'),
        });
      case 'postAssessmentDiscussion':
        return getAdhdPostAssessmentDiscussionLockedTooltip({
          patientDataModel,
          wf,
          isPadDisabled: shouldDisableModule(moduleKey),
        });
      default:
        return '';
    }
  };

  // One-clinician SLaM "End assessment" state. The banner is shown to everyone
  // who can see the overview; the Resume action is limited to the patient's
  // Lead clinician, Org Admins and Support (the roles whose Firestore update
  // rules allow a root-level write on the patient document).
  const assessmentEndRecord = patientDataModel?.assessmentEnd || null;
  const canResumeAssessment =
    Boolean(isOrgAdmin?.()) ||
    Boolean(isSupportUser?.()) ||
    checkIsLeadClinician(patientDataModel, user?.uid);
  
  useEffect(() => {
    if (patientDataModel) {

      // Clinical Interview (exclude hidden sections from denominator)
      const paStage = patientDataModel.dataModel?.['2_clinicalInterview'];
      const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
      const paProgressValue = paStage?.status === 'completed' ? 100 : computeCIProgress(ciSections, excludedKeys);
      setPAProgress(paProgressValue);
  
      // Semi-Structured Interview
      const ssiStage = patientDataModel.dataModel?.['3_semiStructuredInterview'];
      const ssiProgressValue = ssiStage?.status === 'completed' ? 100 : calculateAverageProgress(ssiStage?.sections);
      setSSIProgress(ssiProgressValue);
  
      // Diagnostic Decision — progress is delegated to the shared helper so the
      // Patient Overview card, the resume route, and the DD sidebar all agree.
      // For multi-step orgs (TOC, SLaM, WLC) this is step-based; for everyone
      // else it keeps the coarse 0/50/100 heuristic. When the MDT Plan step
      // exists (Semble MDT + ADHD positive) it uses a 5-step model.
      const ddProgressValue = computeDDModuleProgress({
        patientDataModel,
        isFlatDifferentials: wf.isFlatDifferentials,
        hasMultiStepDD: wf.hasMultiStepDD,
        isTOCWorkflow: wf.isTOCWorkflow,
        isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
        skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
      });
      setDDProgress(ddProgressValue);
      
      // Post-Assessment Discussion
      const padStage = patientDataModel.dataModel?.['5_postAssessmentDiscussion'];
      const padProgressValue = padStage?.status === 'completed' ? 100 : calculateAverageProgress(padStage?.sections);
      setPADProgress(padProgressValue);
     
      // COMMENTED OUT: Track assessment progress
      /*
      trackEvent('assessment_progress_loaded', {
        patientId,
        ciProgress: paProgressValue,
        ssiProgress: ssiProgressValue,
        ddProgress: ddProgressValue,
        padProgress: padProgressValue,
        overallProgress: Math.round((paProgressValue + ssiProgressValue + ddProgressValue + padProgressValue) / 4)
      });
      */
    }
  }, [patientDataModel, patientId, trackEvent, organisationName, wf.isFlatDifferentials, wf.hasMultiStepDD, wf.isTOCWorkflow]);

  if (!patientDataModel) {
    return <div>Loading...</div>;
  }

  const calculateAverageProgress = (sections) => {
    if (!sections) return 0;
    const sectionKeys = Object.keys(sections);
    const totalProgress = sectionKeys.reduce((sum, key) => {
      return sum + (sections[key].progress || 0);
    }, 0);
    const averageProgress = sectionKeys.length ? totalProgress / sectionKeys.length : 0;
    return Math.round(averageProgress);
  };

  const LoadingSpinner = () => (
    <div className="w-full h-24 flex items-center justify-center">
      <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-gray-800 rounded-lg">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="font-semibold">Loading dashboard...</span>
      </div>
    </div>
  );

  // Helper function to render module card based on config
  const renderModuleCard = (moduleKey, progress, dataPath, stage, sections) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig) return null;

    const isDisabled = shouldDisableModule(moduleKey);
    const isLocked = shouldLockModule(moduleKey);
    const disabledReason = getDisabledReason(moduleKey);

    // Build DD start route from a shared helper so Patient Overview entry uses
    // the same eligibility rules as DD pages themselves.
    const ddStartRoute = getDiagnosticDecisionStartRoute({
      patientId,
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isDFDeferred: wf.isDFDeferred,
      isTOCWorkflow: wf.isTOCWorkflow,
      hasMultiStepDD: wf.hasMultiStepDD,
      isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
    });

    const moduleProps = {
      clinicalInterview: {
        clinician: patientDataModel?.clinicians?.clinicianCI?.clinicianName,
        supportingClinicians: [],
        startPath: `/patient/${patientId}/proforma/clinical-interview/patient-information`,
        // For deferred-DF orgs where section 12 is excluded from CI, the DF page
        // has a route guard that redirects away — so "Review" should land on the
        // last real CI section (Risk Assessment). For SLaM (hasCIReview), section 12
        // renders as Review & Synthesis and is the correct review target.
        reviewPath: (wf.isDFDeferred && !wf.hasCIReview)
          ? `/patient/${patientId}/proforma/clinical-interview/risk-assessment`
          : `/patient/${patientId}/proforma/clinical-interview/diagnostic-formulation`,
        docsAvailable: patientDataModel['dataModel']['2_clinicalInterview']?.docs_available,
      },
      semiStructuredInterview: {
        // After a Lead override, show the effective (Lead) clinician on the card
        // pill so ownership is visually clear. Report attribution still uses the
        // original clinicianSSI slot.
        clinician: (() => {
          const effective = getEffectiveSSIClinician(patientDataModel);
          return effective.clinicianName || patientDataModel?.clinicians?.clinicianSSI?.clinicianName;
        })(),
        supportingClinicians: [],
        startPath: `/patient/${patientId}/proforma/semi-structured-interview/symptoms`,
        // For orgs using SSI Review & Synthesis, the SSI module ends with the
        // Review page (replaces both RDD and the old SSI DF page).
        reviewPath: wf.showSSIReviewPage
          ? `/patient/${patientId}/proforma/semi-structured-interview/review`
          : `/patient/${patientId}/proforma/semi-structured-interview/recommended-diagnosis`,
        docsAvailable: patientDataModel['dataModel']['3_semiStructuredInterview']?.docs_available,
      },
      diagnosticDecision: {
        clinician: patientDataModel?.clinicians?.clinicianDD?.clinicianName,
        supportingClinicians: patientDataModel?.clinicians?.clinicianDD?.supportingClinicianNames || [],
        startPath: ddStartRoute,
        // Multi-step DD patients (TOC, SLaM) review via the Review step so
        // the module card "Review" button also points there. Other patients use
        // their standard review route (flat → /adhd, hierarchical → /primary-diagnosis).
        reviewPath: (wf.hasMultiStepDD || wf.isTOCWorkflow)
          ? `/patient/${patientId}/proforma/diagnostic-decision/mdt-review`
          : `/patient/${patientId}/proforma/diagnostic-decision/${(wf.isFlatDifferentials && !wf.isDFDeferred) ? 'adhd' : 'primary-diagnosis'}`,
        docsAvailable: patientDataModel['dataModel']['4_diagnosticDecision']?.docs_available,
      },
      postAssessmentDiscussion: {
        clinician: patientDataModel?.clinicians?.clinicianPAD?.clinicianName,
        supportingClinicians: [],
        startPath: `/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`,
        reviewPath: `/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`,
        docsAvailable: patientDataModel['dataModel']['5_postAssessmentDiscussion']?.docs_available,
      }
    };

    const props = moduleProps[moduleKey] || {};

    // For the DD card on MDT review orgs: show the "+" button to add a supporting
    // clinician when none are currently assigned AND initial clinician assignment is
    // complete (Lead clinician exists). Only Admin/Support users and the Lead
    // Clinician for this patient can see the button — other assigned clinicians cannot.
    const leadClinicianId = patientDataModel?.clinicians?.clinicianLead?.clinicianId;
    const hasCliniciansAssigned = !!leadClinicianId;
    const isAdminOrSupport = claims?.userType === 'Support' || claims?.userType === 'Org. Admin';
    const isLeadClinician = hasCliniciansAssigned && user?.uid === leadClinicianId;
    const showAddSupporting = moduleKey === 'diagnosticDecision' &&
      wf.isMDTReview &&
      hasCliniciansAssigned &&
      (!props.supportingClinicians || props.supportingClinicians.length === 0) &&
      (isAdminOrSupport || isLeadClinician);

    // Determine whether this module card should show an "Assign Clinician" /
    // "Assign Lead" placeholder button when no clinician is assigned. This
    // keeps all module cards the same height and gives Admin/Support users a
    // direct action to open the assignment dialog from the Patient Overview.
    //
    // Three scenarios trigger the button:
    //   1. SLaM with nobody assigned yet — show a CTA on every unassigned card.
    //      Legacy SSI-first patients get "Assign Clinician" (opens the
    //      SSI-vs-Lead chooser); one-clinician CI-first patients get "Assign
    //      Lead" because a single Lead bundle (incl. SSI) is the only action.
    //      Without this, appointment-date rows alone left empty patients with
    //      no visible assignment CTA on the cards.
    //   2. SLaM partial-assignment: SSI clinician exists but Lead is not yet
    //      assigned — show "Assign Lead" on the CI/DD/PAD cards.
    //   3. TOC / other orgs: no clinicians assigned at all — show "Assign
    //      Clinician" on every card so the user can kick off initial assignment.
    const hasAnyClinician = !!(
      patientDataModel?.clinicians?.clinicianSSI?.clinicianId ||
      patientDataModel?.clinicians?.clinicianLead?.clinicianId
    );
    // One-clinician SLaM generation (Oct 2026 preset): module order is CI-first.
    // Legacy SLaM / Urban ADHD stamps are SSI-first and keep the two-step UX.
    const isSlamOneClinician = isSlamOrg(organisationName) && wf.isSSIFirst === false;
    const isSlamAdminUnassignedCard = isSlamOrg(organisationName) &&
      isAdminOrSupport &&
      !props.clinician;
    const isSlamNeedsInitialAssign = isSlamAdminUnassignedCard && !hasAnyClinician;
    const isSlamNeedsLead = isSlamAdminUnassignedCard &&
      hasAnyClinician &&
      !leadClinicianId;
    // Symmetric case: Lead exists but SSI is still unassigned.  Handles the
    // reverse of the normal SLaM SSI-first flow — when an admin assigns the
    // Lead bundle before assigning the SSI clinician.  Only the SSI card
    // triggers this because CI/DD/PAD already show the Lead name in
    // props.clinician, which makes isSlamAdminUnassignedCard false for those.
    const isSlamNeedsSSI = isSlamAdminUnassignedCard &&
      hasAnyClinician &&
      !!leadClinicianId &&
      !patientDataModel?.clinicians?.clinicianSSI?.clinicianId;
    const isNonSlamNeedsClinician = !isSlamOrg(organisationName) &&
      isAdminOrSupport &&
      !hasAnyClinician &&
      !props.clinician;
    const showAssignButton =
      isSlamNeedsInitialAssign || isSlamNeedsLead || isSlamNeedsSSI || isNonSlamNeedsClinician;
    const assignButtonLabel = (isSlamNeedsLead || (isSlamNeedsInitialAssign && isSlamOneClinician))
      ? 'Assign Lead'
      : isSlamNeedsSSI
        ? 'Assign SSI'
        : 'Assign Clinician';

    // Lead SSI override button: shown on the SSI card when the feature is
    // enabled, the SSI is completed, the current user is the Lead (and not
    // already the SSI clinician), and no override is active yet.
    const ssiLeadOverride = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
    const ssiOverrideActive = isSSILeadOverrideActive(patientDataModel);
    const ssiCompleted =
      patientDataModel?.dataModel?.['3_semiStructuredInterview']?.status === 'completed';
    const ssiClinicianId = patientDataModel?.clinicians?.clinicianSSI?.clinicianId;
    // Manual "Take over" from the overview card is currently HIDDEN: the SSI
    // takeover is only ever initiated from the Diagnostic Decision mismatch
    // prompt. The wiring below is kept intact (dialog + handler) and gated
    // behind this flag so it can be re-enabled by flipping it back to true.
    const ENABLE_MANUAL_SSI_TAKEOVER = false;
    const showSSIOverride =
      ENABLE_MANUAL_SSI_TAKEOVER &&
      moduleKey === 'semiStructuredInterview' &&
      wf.allowLeadSSIOverride &&
      ssiCompleted &&
      checkIsLeadClinician(patientDataModel, user?.uid) &&
      user?.uid !== ssiClinicianId &&
      !ssiOverrideActive;

    // Reverse (undo) a takeover — TESTING ONLY. Shown in the same slot as the
    // (now hidden) Take over button, but only in dev/staging, only to the Lead,
    // and only while a takeover is actually active. Lets QA restore the
    // original clinician's state and re-run the flow without a new patient.
    const showSSIReverse =
      moduleKey === 'semiStructuredInterview' &&
      ssiOverrideActive &&
      isNonProdEnvironment() &&
      checkIsLeadClinician(patientDataModel, user?.uid);

    const ssiOverrideInfo =
      moduleKey === 'semiStructuredInterview' && ssiOverrideActive
        ? {
            takenOver: true,
            originalClinicianName:
              ssiLeadOverride?.originalClinicianName ||
              patientDataModel?.clinicians?.clinicianSSI?.clinicianName,
            snapshotPath: ssiLeadOverride?.archiveId
              ? `/patient/${patientId}/ssi-snapshot/${ssiLeadOverride.archiveId}`
              : null,
          }
        : null;

    // Appointment dates: only wire for applicable (non-disabled) modules when
    // the org flag is on. When no human-set date exists, fall back to the
    // London day of firstCompletedAt (same day the report already uses).
    let appointmentDate = null;
    let appointmentDateInferred = false;
    let onEditAppointmentDate = null;
    if (!isDisabled && wf.isAppointmentDatesEnabled) {
      const shortKey = MODULE_KEY_TO_SHORT_KEY[moduleKey];
      const stageKey = MODULE_KEY_TO_STAGE_KEY[moduleKey];
      const stageData = stageKey
        ? patientDataModel?.dataModel?.[stageKey]
        : null;
      const resolved = resolveDisplayAppointmentDate({
        appointmentDate: patientDataModel?.appointmentDates?.[shortKey]?.date,
        firstCompletedAt: stageData?.firstCompletedAt,
        lastCompletedAt: stageData?.lastCompletedAt,
      });
      appointmentDate = resolved.date;
      appointmentDateInferred = resolved.isInferred;
      onEditAppointmentDate = () => setAppointmentDateDialogModule(shortKey);
    }

    return (
      <div className={getAssessmentModuleCardWidthClass(config.moduleOrder.length)} key={moduleKey}>
        <AssessmentModuleCard
          title={moduleConfig.title}
          clinician={props.clinician}
          supportingClinicians={props.supportingClinicians}
          progress={progress}
          patientId={patientId}
          startPath={props.startPath}
          reviewPath={props.reviewPath}
          docsAvailable={props.docsAvailable}
          isLocked={isLocked}
          isDisabled={isDisabled}
          disabledReason={disabledReason}
          circleNumber={moduleConfig.circleNumber}
          lockedTooltip={getLockedTooltip(moduleKey)}
          showAddSupportingButton={showAddSupporting}
          onAddSupportingClick={() => setRequestReassignStep('addDDSupporting')}
          showAssignClinicianButton={showAssignButton}
          onAssignClinicianClick={() => setShowAssignLeadDialog(true)}
          assignClinicianLabel={assignButtonLabel}
          showOverrideButton={showSSIOverride}
          onOverrideClick={() => setShowSSIOverrideDialog(true)}
          overrideInfo={ssiOverrideInfo}
          showReverseButton={showSSIReverse}
          onReverseClick={handleReverseSSIOverride}
          isReversing={isReversingSSI}
          appointmentDate={appointmentDate}
          appointmentDateInferred={appointmentDateInferred}
          onEditAppointmentDate={onEditAppointmentDate}
          // Diagnostic Decision is completed in the clinician's own
          // (non-patient-facing) time in every workflow, so its date block reads
          // just "Date" rather than "Appointment date".
          appointmentDateLabel={
            moduleKey === 'diagnosticDecision' ? 'Date' : 'Appointment date'
          }
          // SLaM PAD advisory — two lasting states:
          // 1) Live mismatch vs DD (staleness cue): amber until decided, then
          //    quieter acknowledged grey; Redo stays available (banner).
          // 2) After a redo: compact "View original" link only (no banner) —
          //    explanation is a hover tooltip, matching Documents Dashboard.
          warningTitle={
            moduleKey === 'postAssessmentDiscussion' && showPadStalenessCue && !padMismatchAcknowledged
              ? 'Outcome changed'
              : null
          }
          warningTone={
            moduleKey === 'postAssessmentDiscussion' && padMismatchAcknowledged
              ? 'acknowledged'
              : 'alert'
          }
          warningNote={
            moduleKey === 'postAssessmentDiscussion' && showPadStalenessCue
              ? padMismatchAcknowledged
                ? 'Reflects a previous outcome (acknowledged).'
                : 'This discussion no longer matches the formal diagnosis.'
              : null
          }
          warningActions={
            moduleKey === 'postAssessmentDiscussion' && showPadStalenessCue ? (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPadRedoDialog(true);
                  }}
                  className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline"
                >
                  Redo discussion
                </button>
                {padRedoArchiveId && (
                  <Link
                    to={`/patient/${patientId}/pad-snapshot/${padRedoArchiveId}`}
                    className="text-xs font-semibold text-gray-600 hover:text-gray-800 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View original
                  </Link>
                )}
              </>
            ) : null
          }
          secondaryLink={
            moduleKey === 'postAssessmentDiscussion' && showPadRedoneNote
              ? {
                  to: `/patient/${patientId}/pad-snapshot/${padRedoArchiveId}`,
                  label: 'View original',
                  tooltipId: 'pad-redone-overview-tip',
                  tooltip:
                    'A previous discussion was archived after the diagnosis changed. This opens the read-only original.',
                }
              : null
          }
        />
      </div>
    );
  };

  // Confirm Lead SSI takeover from the overview card dialog.
  const handleConfirmSSIOverride = async () => {
    setIsOverridingSSI(true);
    try {
      const result = await TriggerOverrideSSI(patientId);
      if (result.success) {
        setShowSSIOverrideDialog(false);
        toast.success(
          'You can now edit the Semi-Structured Interview and review its AI writing.'
        );
        await fetchPatientData(patientId, true);
      } else {
        toast.error(
          result.message ||
            'Could not take over the Semi-Structured Interview. Please try again.'
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          'Could not take over the Semi-Structured Interview. Please try again.'
      );
    } finally {
      setIsOverridingSSI(false);
    }
  };

  // Reverse (undo) a Lead SSI takeover — TESTING ONLY. The button that calls
  // this is only rendered in dev/staging (see showSSIReverse), and the cloud
  // function itself refuses to run in production, so this is a double-gated
  // convenience for QA to restore the original clinician's state and re-run the
  // takeover flow on the same patient rather than provisioning a new one.
  const handleReverseSSIOverride = async () => {
    setIsReversingSSI(true);
    try {
      const result = await TriggerReverseSSIOverride(patientId);
      if (result.success) {
        toast.success(
          'SSI takeover reversed. The original clinician state has been restored.'
        );
        await fetchPatientData(patientId, true);
      } else {
        toast.error(
          result.message ||
            'Could not reverse the Semi-Structured Interview takeover. Please try again.'
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          'Could not reverse the Semi-Structured Interview takeover. Please try again.'
      );
    } finally {
      setIsReversingSSI(false);
    }
  };

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`]
  ];


  return (
    <Layout activePage="dashboard">
      {/* animate-fadeIn is applied to the body (below the nav) only — NOT to
          Layout/TopNavBar. The PatientOverviewSkeleton shown while loading
          renders the same Layout/TopNavBar, so the nav is visually identical
          before and after the swap and must stay rock-stable (fading it would
          make it blink on every load). Fading just the body gently reveals the
          real content as it replaces the skeleton's shimmer. */}
      <div className="flex items-center justify-between mb-4 animate-fadeIn">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      <div className="flex-grow pl-8 pr-8 animate-fadeIn">
        {patientDataModel ? (
          <>
            <PatientInfoPanel 
              patientDataModel={patientDataModel} 
              patientId={patientId} 
              informantData={informantData}
              refreshPatientData={() => fetchPatientData(patientId, true)}
              isSubclinicalService={isSubclinicalService}
              requestReassignStep={requestReassignStep}
              onReassignStepHandled={() => setRequestReassignStep(null)}
              // Opens the shared AppointmentDateDialog without auto-focusing a
              // specific module's date picker (card "Add Date" buttons still pass
              // a short key). Only wired when the org has appointment dates on.
              onManageAppointmentDates={
                wf.isAppointmentDatesEnabled
                  ? () => setAppointmentDateDialogModule('all')
                  : undefined
              }
            />

            {/* Hide Assessment Modules for subclinical services */}
            {!isSubclinicalService && (
              <>
                <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">Assessment Modules</div>

                {/* One-clinician SLaM: explains the locked post-CI cards and
                    offers Resume to the Lead / Org Admin / Support. Rendered only
                    when the decision is actually set so other workflows never
                    mount the (collapsed) banner. */}
                {wf.assessmentEndedAfterCI && (
                  <div className="mb-4">
                    <AssessmentEndedBanner
                      isVisible
                      patientId={patientId}
                      assessmentEnd={assessmentEndRecord}
                      canResume={canResumeAssessment}
                      currentUser={user}
                      onResumed={() => fetchPatientData(patientId, true)}
                    />
                  </div>
                )}

                <div className="w-full justify-between items-stretch gap-1 inline-flex">                        
                  {/* Render modules in configured order */}
                  {config.moduleOrder.map(moduleKey => {
                    const progressMap = {
                      clinicalInterview: PAProgress,
                      semiStructuredInterview: SSIProgress, 
                      diagnosticDecision: DDProgress,
                      postAssessmentDiscussion: PADProgress
                    };
                    
                    return renderModuleCard(moduleKey, progressMap[moduleKey], null, null, null);
                  })}
                </div>
              </>
            )}

            {/* SLaM Lead assignment dialog — opened from "Assign Lead" buttons on
                module cards when a patient has SSI assigned but no Lead yet.
                Key forces a clean remount so internal dialog state resets between opens. */}
            {showAssignLeadDialog && (
              <AssignCliniciansDialog
                key={`assign-lead-${patientId}`}
                isOpen={showAssignLeadDialog}
                onClose={() => {
                  setShowAssignLeadDialog(false);
                  fetchPatientData(patientId, true);
                }}
                patientId={patientId}
                patientDataModel={patientDataModel}
                refreshPatientData={() => fetchPatientData(patientId, true)}
              />
            )}

            {/* SLaM PAD redo — opened from the Post-Assessment Discussion card's
                staleness cue. The clinician has already decided to redo, so the
                dialog opens straight on the redo details step (new appointment
                date). On success it re-fetches and lands on the PAD Review page. */}
            <PadRedoDialog
              isOpen={showPadRedoDialog}
              onClose={() => setShowPadRedoDialog(false)}
              patientId={patientId}
              patientDataModel={patientDataModel}
              mode="redo"
              onBeforeRedo={flushPendingUpdates}
              onAfterRedo={async () => {
                await fetchPatientData(patientId, true);
                setShowPadRedoDialog(false);
                navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
              }}
            />

            {/* Lead SSI override confirmation — shared dialog used from the
                SSI module card (and mirrored on the DD consistency banner). */}
            <SSIOverrideDialog
              isOpen={showSSIOverrideDialog}
              onClose={() => setShowSSIOverrideDialog(false)}
              onConfirm={handleConfirmSSIOverride}
              isOverriding={isOverridingSSI}
              ssiClinicianName={
                patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
                'the SSI clinician'
              }
            />

            {/* Per-module appointment dates (SLaM / 3 Borough). Only mounted
                when the org flag is on. editableModules excludes disabled /
                N/A modules so their dates stay hidden and cannot be set. */}
            {wf.isAppointmentDatesEnabled && (
              <AppointmentDateDialog
                isOpen={appointmentDateDialogModule !== null}
                onClose={() => setAppointmentDateDialogModule(null)}
                patientId={patientId}
                appointmentDates={patientDataModel?.appointmentDates || {}}
                // Inferred London days from completion timestamps — shown and
                // editable in the dialog, but not written until the user acts.
                inferredDates={Object.fromEntries(
                  Object.entries(MODULE_KEY_TO_STAGE_KEY).map(([moduleKey, stageKey]) => {
                    const shortKey = MODULE_KEY_TO_SHORT_KEY[moduleKey];
                    const stageData = patientDataModel?.dataModel?.[stageKey];
                    const { date, isInferred } = resolveDisplayAppointmentDate({
                      appointmentDate: patientDataModel?.appointmentDates?.[shortKey]?.date,
                      firstCompletedAt: stageData?.firstCompletedAt,
                      lastCompletedAt: stageData?.lastCompletedAt,
                    });
                    return [shortKey, isInferred ? date : null];
                  })
                )}
                moduleOrder={config.moduleOrder}
                modules={config.modules}
                refreshPatientData={() => fetchPatientData(patientId)}
                // 'all' means Actions menu — list every editable module, no picker pop.
                initialModule={
                  appointmentDateDialogModule && appointmentDateDialogModule !== 'all'
                    ? appointmentDateDialogModule
                    : null
                }
                editableModules={config.moduleOrder
                  .filter((key) => !shouldDisableModule(key))
                  .map((key) => MODULE_KEY_TO_SHORT_KEY[key])
                  .filter(Boolean)}
                organisation={organisationName}
                manageReports={patientDataModel?.dataModel?.['6_manageReports'] || {}}
                documentsRoute={`/patient/${patientId}/documents`}
              />
            )}

            <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">Pre-Assessment Information</div>

            {patientDataModel['dataModel']['1_preAppointmentInformation']?.status === 'noData' && (
              <StatusMessage
                color="gray"
                icon={<InformationCircleIcon className="w-5 h-5 mr-2" />}
              >
                Information will become available here as the patient completes their pre-assessment questionnaires.
              </StatusMessage>
            )}

            {patientDataModel['dataModel']['1_preAppointmentInformation']?.status === 'withData' && (
              <PreAppointmentDashboardWithData 
                // Force a clean remount of the panel subtree whenever patientId changes.
                // This prevents any lingering internal component state (e.g., expanded rows,
                // selected forms) from carrying over to the next patient's view.
                key={patientId}
                patientDataModel={patientDataModel}
                informantData={informantData}
                isSubclinicalService={isSubclinicalService}
              />
            )}
          </>
        ) : (
          <LoadingSpinner />
        )}
      </div>


    </Layout>
  );
}

export default PatientOverview;